import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  TreeAction,
  TreeActionTypes,
  TreeDeleteNodeAction,
  TreeDeleteNodeErrorAction,
  TreeDeleteNodeSuccessAction,
  TreeExpandNodeAction,
  TreeInsertNodeAction,
  TreeLoadNodesAction,
  TreeLoadNodesErrorAction,
  TreeLoadNodesSuccessAction,
  TreeLoadPathAction,
  TreeMoveNodeAction,
  TreeMoveNodeErrorAction,
  TreeMoveNodeSuccessAction,
  TreeRegisterAction,
  TreeSaveNodeAction,
  TreeSaveNodeErrorAction,
  TreeSaveNodeSuccessAction,
  TreeSetAllNodesAction
} from './treeActions.service';
import {IOuterNode} from '../interfaces/IOuterNode';
import {combineLatest, Observable, of} from 'rxjs';
import {ITreeActionPayload, ITreeConfiguration, ITreeData, ITreeState} from './ITreeState';
import {NodeDispatcherService} from '../service/nodesDispatcher.service';
import {catchError, filter, map, mergeMap, switchMap, take} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {NEW_NODE_ID, treeConfigurationSelector, treeSelector} from './treeReducer';

@Injectable()
export class TreeEffectsService {

  public register$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_REGISTER),
      map((action: TreeRegisterAction): TreeAction => {
        if (action.payload.silent) {
          return new TreeSetAllNodesAction({treeId: action.payload.treeId, nodes: action.payload.nodes});
        } else {
          return new TreeLoadNodesAction({treeId: action.payload.treeId, id: null});
        }
      })
    )
  );

  public load$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_LOAD),
      mergeMap((action: TreeLoadNodesAction) => this.loadNodes(action.payload.treeId, action.payload.id)
        .pipe(
          map((nodesData: IOuterNode[]): TreeLoadNodesSuccessAction => new TreeLoadNodesSuccessAction({
            treeId: action.payload.treeId,
            id: action.payload.id,
            nodes: nodesData
          })),
          catchError(() => of(new TreeLoadNodesErrorAction({
            treeId: action.payload.treeId,
            id: action.payload.id
          })))
        )
      )
    )
  );


  public delete$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_DELETE_NODE),
      switchMap((action: TreeDeleteNodeAction) => this.deleteNode(action.payload.treeId, action.payload.node)
        .pipe(
          map((): TreeDeleteNodeSuccessAction => new TreeDeleteNodeSuccessAction({...action.payload})),
          catchError((): Observable<TreeDeleteNodeErrorAction> => of(new TreeDeleteNodeErrorAction({...action.payload})))
        )
      )
    )
  );


  public save$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_SAVE_NODE),
      switchMap((action: TreeSaveNodeAction) => this.saveNode(action.payload.treeId, {...action.payload.node})
        .pipe(
          map((node: IOuterNode): TreeSaveNodeSuccessAction => new TreeSaveNodeSuccessAction({
            treeId: action.payload.treeId,
            oldNode: action.payload.node,
            node
          })),
          catchError(() => of(new TreeSaveNodeErrorAction({...action.payload})))
        )
      )
    )
  );

  public expand$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_EXPAND_NODE),
      switchMap((action: TreeExpandNodeAction) =>
        this.store
          .pipe(
            select(treeSelector(action.payload.treeId)),
            take(1),
            filter((treeState: ITreeData) => !treeState.configuration.isFullyLoaded),
            map(() => {
              return new TreeLoadNodesAction({
                  treeId: action.payload.treeId,
                  id: action.payload.id
                }
              );
            })
          )
      )
    )
  );

  public insert$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_INSERT_NODE),
      filter((action: TreeInsertNodeAction) => !!action.payload.parentId),
      map((action: TreeInsertNodeAction) => {
        return new TreeExpandNodeAction({treeId: action.payload.treeId, id: action.payload.parentId});
      })
    )
  );

  public initPathForFullyLoadedTreeEffect$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_LOAD_PATH),
      switchMap((action: TreeLoadPathAction) => {
        return this.store.select(treeConfigurationSelector(action.payload.treeId))
          .pipe(
            take(1),
            // @ts-ignore
            map((configuration: ITreeConfiguration) => {
              return {action, configuration};
            })
          );
      }),
      map((value: { action: TreeLoadPathAction, configuration: ITreeConfiguration }) => {
          const {action, configuration} = value;

          if (configuration.isFullyLoaded) {
            return action.payload.ids.map((id: string) => new TreeExpandNodeAction({treeId: action.payload.treeId, id}));
          } else {
            const loadActions = action.payload.ids.map((id: string) => this.loadNodes(action.payload.treeId, id));
            return combineLatest(loadActions)
              .pipe(
                take(1),
                mergeMap((data: IOuterNode[][]) => {
                  const loadSuccess = data.map((nodes: IOuterNode[], index) => new TreeLoadNodesSuccessAction({
                    treeId: action.payload.treeId,
                    id: action.payload.ids[index],
                    nodes
                  }));
                  const expandNodes = action.payload.ids.map((id: string) => new TreeExpandNodeAction({
                    treeId: action.payload.treeId,
                    id
                  }));

                  return [...loadSuccess, ...expandNodes];
                })
              );
          }
        }
      ),
      mergeMap((actions: any[]) => actions)
    )
  );

  constructor(public actions$: Actions,
              public nodeDispatcherService: NodeDispatcherService,
              public store: Store<ITreeState>) {
  }

  protected deleteNode(treeId: string, node: IOuterNode): Observable<IOuterNode> {
    const nodeService = this.nodeDispatcherService.get(treeId);

    return node.id ? nodeService.remove(node.id) : of(node);
  }

  protected loadNodes(treeId: string, id: string | null) {
    const nodeService = this.nodeDispatcherService.get(treeId);

    return nodeService.load(id);
  }

  protected saveNode(treeId: string, node: IOuterNode): Observable<IOuterNode> {
    const nodeService = this.nodeDispatcherService.get(treeId);

    if (node.id === NEW_NODE_ID) {
      return nodeService.add(node, node.parentId);
    } else {
      return nodeService.update(node);
    }
  }

  protected moveNode(treeId: string, source: IOuterNode, target: IOuterNode): Observable<IOuterNode> {
    const nodeService = this.nodeDispatcherService.get(treeId);

    return nodeService.move(source, target);
  }
}
